import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
  Button,
  Chip,
  Checkbox,
  styled,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Switch,
  Tooltip,
  IconButton,
  Link,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { getUser } from '@/lib/common';
import SearchInput from '@/components/resource-allocation/search-input';
import { AppDataGrid } from '@/components/ui';
import { AppCard } from '@/components/app-card/app-card';
import ReasonForRejectionDialog from './reason-for-rejection-dialog';
import RecommendationsListModal from './recommendations-list-modal';
import { useAttestation } from './hooks/useAttestation';
import { GridColDef } from '@mui/x-data-grid';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { DisputeDialog } from '@/components/dispute-dialog/dispute-dialog';
import DisputesModal from '@/components/disputes-table/disputes-modal';
import { UserDto } from '@cloudcost/shared-types/users';
import { Pill, DsIcon } from '@/components/ds';
import { statusTone, T } from '@/theme/tokens';
import ResourceDetailsModal from '@/components/resource-details/resource-details-modal';

const CardContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const SelectedResourcesInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  alignItems: 'flex-start',
  marginTop: 20,
});

const ResourceGroupChipContainer = styled('div')({
  display: 'flex',
  marginBottom: 20,
  width: '100%',
  flexWrap: 'wrap',
  gap: 4,
});

const PaginationContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: 20,
});

const MainContentContainer = styled('div')({
  margin: '12px',
});

const FilterContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  marginBottom: '5px',
  gap: 10,
});

const SwitchBox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

interface AssignmentSectionProps {
  children: React.ReactNode;
  isReadonly: boolean;
  formResources: Map<string, any[]>;
  resourcesLength: number;
  resourcesSelected: boolean;
  handleAttestatation: () => void;
  handleRejectClick: () => void;
  handleOpenDisputeDialog: () => void;
}

const AssignmentSection = ({
  children,
  isReadonly,
  formResources,
  resourcesLength,
  resourcesSelected,
  handleAttestatation,
  handleRejectClick,
  handleOpenDisputeDialog,
}: AssignmentSectionProps) => {
  return (
    <Box>
      {children}

      {!isReadonly && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ marginTop: 4, pl: 2, pr: 2, justifyContent: 'space-between' }}>
          <SelectedResourcesInfo style={{ flex: 1 }}>
            {formResources.size > 0 && <b>Selected resources</b>}
            {[...formResources.keys()].map((rgName) => (
              <Typography variant="caption" key={rgName} align="left">
                -{' '}
                {rgName.length > 40 ? rgName.substring(0, 40) + '...' : rgName}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ marginTop: 1 }}>
              {resourcesLength} selected resources
            </Typography>
          </SelectedResourcesInfo>

          <Box
            sx={{
              flex: 1,
              justifyContent: 'flex-end',
              display: 'flex',
              gap: 2,
              height: '50px',
            }}>
            <Button
              variant="contained"
              onClick={handleAttestatation}
              disabled={!resourcesSelected}
              color="primary">
              Confirm Selected
            </Button>

            <Button
              variant="contained"
              onClick={handleRejectClick}
              disabled={!resourcesSelected}
              color="error">
              Reject Selected
            </Button>

            <Button
              variant="contained"
              onClick={handleOpenDisputeDialog}
              disabled={!resourcesSelected}
              color="warning">
              Dispute
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

const baseColumns = (onResourceClick: (resourceId: number) => void): GridColDef[] => [
  {
    field: 'resource_name',
    headerName: 'Resource Name',
    flex: 1,
    renderCell: (params: any) => {
      const resourceId = params.row.resource_id || params.row.id;
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) => {
            e.stopPropagation();
            onResourceClick(resourceId);
          }}
          sx={{
            textAlign: 'left',
            color: T.primary,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
            background: 'none',
            border: 'none',
            padding: 0,
          }}>
          {params.value}
        </Link>
      );
    },
  },
  {
    field: 'resource_type',
    headerName: 'Resource Type',
    flex: 1,
  },
  {
    field: 'cost',
    headerName: 'Forecast',
    width: 100,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (params: any) => formatCurrencyNoUnit(Number(params.value || 0)),
    renderCell: (params: any) => (
      <span style={{ fontWeight: 700 }}>{params.formattedValue}</span>
    ),
  },
  {
    field: 'recommendations',
    headerName: '$',
    width: 35,
    sortable: false,
    filterable: false,
    renderCell: (params: any) => {
      const recommendations = params.row.recommendations || [];
      if (recommendations.length === 0) {
        return null;
      }
      
      return (
        <Tooltip title={`${recommendations.length} active recommendation${recommendations.length > 1 ? 's' : ''}`}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              // This will be handled by the parent component
              params.api.getRow(params.id).__recommendationsClick?.(recommendations, params.row.resource_name);
            }}
            sx={{ 
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(242,142,44,0.10)'
              }
            }}
          >
            <DsIcon name="dollar" size={18} style={{ color: T.warn }} />
          </IconButton>
        </Tooltip>
      );
    },
  },
  {
    field: 'Status',
    filterable: true,
    headerName: 'Status',
    width: 130,
    type: 'singleSelect',
    valueOptions: ['PENDING', 'CONFIRMED', 'REJECTED'],
    // Display only: render the status value as a design-system Pill. The
    // singleSelect type / valueOptions / filtering behavior are unchanged.
    renderCell: (params: any) => {
      const value = params.value ?? 'PENDING';
      if (!value) return null;
      return <Pill tone={statusTone(value)}>{value}</Pill>;
    },
  },
];

const getColumnsForGrouping = (groupBy: 'hierarchy' | 'systems', onResourceClick: (resourceId: number) => void) => {
  const cols = baseColumns(onResourceClick);
  const dynamicColumn =
    groupBy === 'hierarchy'
      ? {
          field: 'system_name',
          headerName: 'System',
          width: 150,
          valueGetter: (params: any) => params.row.system_name || '',
        }
      : {
          field: 'subscription_name',
          headerName: 'Subscription',
          width: 150,
          valueGetter: (params: any) => params.row.subscription_name || '',
        };

  return [
    cols[0], // Resource Name
    cols[1], // Resource Type
    dynamicColumn, // System or Subscription
    cols[2], // Forecast
    cols[3], // Recommendations ($)
    cols[4], // Status (rendered as a colored Pill)
  ];
};

interface AttestationTableProps {
  groupBy?: 'hierarchy' | 'systems';
  onGroupByChange?: (groupBy: 'hierarchy' | 'systems') => void;
  display: 'all' | 'managed';
  isReadonly?: boolean;
  data: {
    users: any[];
    systems: any[];
    departments: any[];
    cloudAccounts: any[];
    resources: any[];
    resourceGroups: any[];
    rejectedResources: any[];
  };
  onDataChange?: () => void;
}

function AttestationTable({
  groupBy = 'systems',
  onGroupByChange,
  display,
  isReadonly = false,
  data,
  onDataChange,
}: Readonly<AttestationTableProps>) {
  const { attestateResources, rejectResources } = useAttestation();

  const [formResources, setFormResources] = useState(new Map());
  const [alert, setAlert] = useState(false);
  const [pageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [tagMap, setTagMap] = useState<Map<string, any[]>>(new Map());
  const [tagsLoading, setTagsLoading] = useState(false);

  const [resourceFilter, setResourceFilter] = useState('');
  const [totalPages, setTotalPages] = useState(0);

  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [selectedResourcesForRejection, setSelectedResourcesForRejection] =
    useState<any[]>([]);

  const [recommendationsModalOpen, setRecommendationsModalOpen] = useState(false);
  const [selectedResourceRecommendations, setSelectedResourceRecommendations] = useState<{
    recommendations: any[];
    resourceName: string;
  } | null>(null);

  const [expandedSubscriptions, setExpandedSubscriptions] = useState<
    Map<string, boolean>
  >(new Map());
  const [expandedResourceGroups, setExpandedResourceGroups] = useState<
    Map<string, boolean>
  >(new Map());
  const [expandedSystems, setExpandedSystems] = useState<Map<string, boolean>>(
    new Map(),
  );

  const [showNonPendingResources, setShowNonPendingResources] = useState(false);

  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [dispute, setDispute] = useState<any[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserDto[]>([]);
  const [defaultReceiverId, setDefaultReceiverId] = useState<number | null>(null);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);

  const items = useMemo(() => {
    let filteredResources = data.resources;
    
    if (display === 'managed') {
      filteredResources = data.resources.filter((resource: any) => {
        const currentUser = getUser();
        return (
          resource.username === currentUser?.username ||
          resource.owner_name === currentUser?.username
        );
      });
    }

    // Filter by status if showNonPendingResources is false
    if (!showNonPendingResources) {
      filteredResources = filteredResources.filter((resource: any) => 
        resource.Status === 'PENDING' || !resource.Status
      );
    }

    return filteredResources;
  }, [data.resources, display, showNonPendingResources]);

  const resourceGroups = data.resourceGroups;

  const handleRejectClick = () => {
    const resourceIds = [...formResources.values()]
      .flat()
      .map((resource) => resource.id);

    if (resourceIds.length === 0) {
      console.warn('No resources selected for rejection.');
      return;
    }

    setSelectedResourcesForRejection([...formResources.values()].flat());
    setRejectionDialogOpen(true);
  };

  const handleRejectDialogClose = () => {
    setRejectionDialogOpen(false);
    setSelectedResourcesForRejection([]);
  };

  const handleRejectConfirm = async (reason: string) => {
    try {
      const resourceIds = selectedResourcesForRejection.map(
        (resource) => resource.id,
      );

      await rejectResources(resourceIds, reason);
      setFormResources(new Map());
      setRejectionDialogOpen(false);
      setSelectedResourcesForRejection([]);

      onDataChange?.();
    } catch (error) {
      console.error('Error rejecting resources:', error);
    }
  };

  const handleRecommendationsClick = (recommendations: any[], resourceName: string) => {
    setSelectedResourceRecommendations({ recommendations, resourceName });
    setRecommendationsModalOpen(true);
  };

  const handleRecommendationsModalClose = () => {
    setRecommendationsModalOpen(false);
    setSelectedResourceRecommendations(null);
  };

  // Helper function to check if a resource group has recommendations
  const hasRecommendations = (resources: any[]) => {
    return resources.some((resource: any) => 
      resource.recommendations && resource.recommendations.length > 0
    );
  };

  // Helper function to count total recommendations in a resource group
  const getRecommendationCount = (resources: any[]) => {
    return resources.reduce((count: number, resource: any) => 
      count + (resource.recommendations?.length || 0), 0
    );
  };

  const resourcesMap = useMemo(() => {
    const map = new Map();

    // Key by resource_group_id so same-named RGs in different subscriptions don't collide
    const rgById = new Map();
    resourceGroups.forEach((rg: any) => {
      rgById.set(rg.id, rg);
    });

    for (const item of items) {
      const rgId = item.resource_group_id;
      const rg = rgId != null ? rgById.get(rgId) : null;
      const subscriptionKey =
        rg?.subscription_name ?? item.subscription_name ?? 'Unassigned';

      if (!map.has(subscriptionKey)) {
        map.set(subscriptionKey, new Map());
      }
      const rgKey = rgId ?? item.resource_group_name;
      if (!map.get(subscriptionKey).has(rgKey)) {
        map.get(subscriptionKey).set(rgKey, []);
      }
      map.get(subscriptionKey).get(rgKey).push(item);
    }

    return map;
  }, [items, resourceGroups]);

  const systemsMap = useMemo(() => {
    const map = new Map();

    for (const item of items) {
      const systemKey = item.system_name || 'Unassigned';
      const rgKey = item.resource_group_id ?? item.resource_group_name;

      if (!map.has(systemKey)) {
        map.set(systemKey, new Map());
      }
      if (!map.get(systemKey).has(rgKey)) {
        map.get(systemKey).set(rgKey, []);
      }
      map.get(systemKey).get(rgKey).push(item);
    }

    return map;
  }, [items]);

  const getResourceGroupsForSubscription = useCallback(
    (subscription: any) => {
      const subscriptionGroups = resourceGroups.filter(
        (rg: any) => rg.subscription_name === subscription,
      );

      const lowerCaseString = resourceFilter.toLowerCase();

      const groupsWithResources = subscriptionGroups
        .map((rg: any) => {
          const resources =
            resourcesMap.get(subscription)?.get(rg.id) ?? [];

          const filteredResources = resources.filter(
            (resource: any) =>
              resource.resource_name.toLowerCase().includes(lowerCaseString) ||
              resource.resource_type.toLowerCase().includes(lowerCaseString) ||
              resource.system_name?.toLowerCase().includes(lowerCaseString) ||
              resource.username?.toLowerCase().includes(lowerCaseString),
          );

          const resourceGroupCost = filteredResources.reduce(
            (sum: number, resource: any) => sum + Number(resource.cost ?? 0),
            0,
          );

          return {
            ...rg,
            resources: filteredResources,
            cost: resourceGroupCost,
          };
        })
        .filter((rg: any) => rg.resources.length > 0);

      const subscriptionCost = groupsWithResources.reduce(
        (sum: number, rg: any) => sum + Number(rg.cost ?? 0),
        0,
      );

      return {
        groups: groupsWithResources,
        totalCost: subscriptionCost,
      };
    },
    [resourceGroups, resourcesMap, resourceFilter],
  );

  const getResourceGroupsForSystem = useCallback(
    (systemName: any) => {
      const lowerCaseString = resourceFilter.toLowerCase();

      const systemResourceGroups = new Map();

      if (systemsMap.has(systemName)) {
        const resourceGroupsInSystem = systemsMap.get(systemName);

        for (const [rgKey, resources] of resourceGroupsInSystem.entries()) {
          const filteredResources = resources.filter(
            (resource: any) =>
              resource.resource_name.toLowerCase().includes(lowerCaseString) ||
              resource.resource_type.toLowerCase().includes(lowerCaseString) ||
              resource.system_name?.toLowerCase().includes(lowerCaseString) ||
              resource.username?.toLowerCase().includes(lowerCaseString),
          );

          if (filteredResources.length > 0) {
            const resourceGroupCost = filteredResources.reduce(
              (sum: number, resource: any) => sum + Number(resource.cost ?? 0),
              0,
            );

            const rgMeta =
              typeof rgKey === 'number'
                ? resourceGroups.find((rg: any) => rg.id === rgKey)
                : resourceGroups.find(
                    (rg: any) => rg.resource_group_name === rgKey,
                  );
            const meta = rgMeta || { resource_group_name: String(rgKey) };

            systemResourceGroups.set(rgKey, {
              ...meta,
              id: meta.id ?? rgKey,
              resource_group_name: meta.resource_group_name ?? String(rgKey),
              resources: filteredResources,
              cost: resourceGroupCost,
            });
          }
        }
      }

      const groups = Array.from(systemResourceGroups.values());
      const totalCost = groups.reduce(
        (sum: number, rg: any) => sum + Number(rg.cost ?? 0),
        0,
      );

      return {
        groups,
        totalCost,
      };
    },
    [systemsMap, resourceGroups, resourceFilter],
  );

  const filteredSubscriptions = useMemo(() => {
    if (resourceGroups.length === 0) return [];

    const filterText = resourceFilter.toLowerCase();

    if (!filterText || filterText.length <= 2) {
      const uniqueSubscriptionNames = [
        ...new Set(resourceGroups.map((rg: any) => rg.subscription_name)),
      ].filter(
        (subName) =>
          getResourceGroupsForSubscription(subName).groups.length > 0,
      );

      return uniqueSubscriptionNames.sort((a, b) => {
        const costA = getResourceGroupsForSubscription(a)?.totalCost ?? 0;
        const costB = getResourceGroupsForSubscription(b)?.totalCost ?? 0;
        return costB - costA;
      });
    } else {
      const uniqueSubscriptionNames = [
        ...new Set(resourceGroups.map((rg: any) => rg.subscription_name)),
      ];

      const matchingSubscriptions = uniqueSubscriptionNames.filter(
        (subName) => {
          const { groups } = getResourceGroupsForSubscription(subName);
          const subscriptionNameMatches = (subName as string)
            .toLowerCase()
            .includes(filterText);
          const hasMatchingResourcesInGroups = groups.length > 0;

          return subscriptionNameMatches || hasMatchingResourcesInGroups;
        },
      );

      return matchingSubscriptions.sort((a, b) => {
        const costA = getResourceGroupsForSubscription(a)?.totalCost ?? 0;
        const costB = getResourceGroupsForSubscription(b)?.totalCost ?? 0;
        return costB - costA;
      });
    }
  }, [resourceGroups, resourceFilter, getResourceGroupsForSubscription]);

  const filteredSystems = useMemo(() => {
    if (items.length === 0) return [];

    const filterText = resourceFilter.toLowerCase();
    const uniqueSystemNames = [
      ...new Set(items.map((item: any) => item.system_name || 'Unassigned')),
    ];

    if (!filterText || filterText.length <= 2) {
      return uniqueSystemNames
        .filter(
          (systemName) =>
            getResourceGroupsForSystem(systemName).groups.length > 0,
        )
        .sort((a, b) => {
          const costA = getResourceGroupsForSystem(a)?.totalCost ?? 0;
          const costB = getResourceGroupsForSystem(b)?.totalCost ?? 0;
          return costB - costA;
        });
    } else {
      const matchingSystems = uniqueSystemNames.filter((systemName) => {
        const { groups } = getResourceGroupsForSystem(systemName);
        const systemNameMatches = (systemName as string)
          .toLowerCase()
          .includes(filterText);
        const hasMatchingResourcesInGroups = groups.length > 0;

        return systemNameMatches || hasMatchingResourcesInGroups;
      });

      return matchingSystems.sort((a, b) => {
        const costA = getResourceGroupsForSystem(a)?.totalCost ?? 0;
        const costB = getResourceGroupsForSystem(b)?.totalCost ?? 0;
        return costB - costA;
      });
    }
  }, [items, resourceFilter, getResourceGroupsForSystem]);

  useEffect(() => {
    if (groupBy === 'hierarchy') {
      setTotalPages(Math.ceil(filteredSubscriptions.length / pageSize));
    } else {
      setTotalPages(Math.ceil(filteredSystems.length / pageSize));
    }
  }, [filteredSubscriptions, filteredSystems, pageSize, groupBy]);

  const paginatedItems = useMemo(() => {
    const start = currentPage * pageSize;
    if (groupBy === 'hierarchy') {
      return filteredSubscriptions.slice(start, start + pageSize);
    } else {
      return filteredSystems.slice(start, start + pageSize);
    }
  }, [filteredSubscriptions, filteredSystems, currentPage, pageSize, groupBy]);

  const handleSubscriptionToggle = useCallback(
    (subscriptionName: string, isExpanded: boolean) => {
      setExpandedSubscriptions((prev) => {
        const newMap = new Map(prev);
        newMap.set(subscriptionName, isExpanded);
        return newMap;
      });
    },
    [],
  );

  const handleSystemToggle = useCallback(
    (systemName: string, isExpanded: boolean) => {
      setExpandedSystems((prev) => {
        const newMap = new Map(prev);
        newMap.set(systemName, isExpanded);
        return newMap;
      });
    },
    [],
  );

  const getTags = useCallback(
    async (resourceGroupId: string) => {
      if (tagMap.has(resourceGroupId) ?? tagsLoading) {
        return tagMap.get(resourceGroupId);
      }

      setTagsLoading(true);
      try {
        const tags = await fetchWrapper.get(
          urls.DevOps.getRGTags.replace(':id', resourceGroupId),
        );
        setTagMap((prevTagMap) =>
          new Map(prevTagMap).set(resourceGroupId, tags),
        );
        return tags;
      } catch (error) {
        console.error('Error fetching tags:', error);
        setTagMap((prevTagMap) => new Map(prevTagMap).set(resourceGroupId, []));
        return [];
      } finally {
        setTagsLoading(false);
      }
    },
    [tagMap, tagsLoading],
  );

  const handleResourceGroupToggle = useCallback(
    async (resourceGroupId: string, isExpanded: boolean) => {
      setExpandedResourceGroups((prev) => {
        const newMap = new Map(prev);
        newMap.set(resourceGroupId, isExpanded);
        return newMap;
      });

      if (isExpanded) {
        await getTags(resourceGroupId);
      }
    },
    [getTags],
  );

  const handleSelectionChange = useCallback(
    (ids: any[], rg: { id?: number; resource_group_name: string; resources: any[] }) => {
      const currentRgResourceIds = new Set(rg.resources.map((item) => item.id));
      const validSelectedIds = ids.filter((id) => currentRgResourceIds.has(id));

      const selectedRowData = items.filter((row: any) =>
        validSelectedIds.includes(row.id),
      );

      const rgKey = rg.id ?? rg.resource_group_name;
      setFormResources((prevMap) => {
        const newMap = new Map(prevMap);
        if (selectedRowData.length > 0) {
          newMap.set(rgKey, selectedRowData);
        } else {
          newMap.delete(rgKey);
        }
        return newMap;
      });
    },
    [items],
  );

  const handleAttestatation = useCallback(async () => {
    const resourceIds = [...formResources.values()]
      .flat()
      .map((resource) => resource.id);

    if (resourceIds.length === 0) {
      console.warn('No resources selected for assignment.');
      return;
    }

    try {
      await attestateResources(resourceIds);
      setAlert(true);
      setFormResources(new Map());
      setExpandedSubscriptions(new Map());
      setExpandedResourceGroups(new Map());

      onDataChange?.();
    } catch (error) {
      console.error(`Error assigning:`, error);
    }
  }, [formResources, attestateResources, onDataChange]);

  const resourcesLength = useMemo(() => {
    return [...formResources.values()].flat().length;
  }, [formResources]);

  const handleDebouncedSearchChange = useCallback(
    (value: string) => {
      setResourceFilter(value);
      setCurrentPage(0);
    },
    [setResourceFilter, setCurrentPage],
  );

  const handleStatusSwitchChange = useCallback((checked: boolean) => {
    setShowNonPendingResources(checked);
    setFormResources(new Map());
    setExpandedSubscriptions(new Map());
    setExpandedResourceGroups(new Map());
    setExpandedSystems(new Map());
  }, []);

  const resourcesSelected = resourcesLength > 0;

  const handleGroupByChange = (
    _event: React.MouseEvent<HTMLElement>,
    newGroupBy: 'hierarchy' | 'systems' | null,
  ) => {
    if (newGroupBy !== null && onGroupByChange) {
      onGroupByChange(newGroupBy);
    }
  };

  const handleResourceClick = useCallback((resourceId: number) => {
    setSelectedResourceId(resourceId);
    setResourceModalOpen(true);
  }, []);

  const handleOpenDisputeDialog = useCallback(async () => {
    const selectedResources = [...formResources.values()].flat();
    const resourceIds = selectedResources.map((resource) => resource.id);

    if (resourceIds.length === 0) {
      return;
    }

    try {
      const users = await fetchWrapper.get(urls.DevOps.getUsersAll);
      setAvailableUsers(users);

      // Extract unique owner IDs from selected resources
      const ownerIds = selectedResources
        .map((r) => r.owner_id || r.assigned_owner_id)
        .filter((id) => id !== null && id !== undefined && id !== -1);

      // Determine if single owner or multiple/none
      if (ownerIds.length === 0) {
        // No owners - show selector
        setDefaultReceiverId(null);
        setShowUserSelector(true);
      } else {
        const uniqueOwnerIds = [...new Set(ownerIds)];
        if (uniqueOwnerIds.length === 1) {
          // Single owner - use as default
          setDefaultReceiverId(uniqueOwnerIds[0]);
          setShowUserSelector(false);
        } else {
          // Multiple owners - show selector
          setDefaultReceiverId(null);
          setShowUserSelector(true);
        }
      }

      setDisputeDialogOpen(true);
    } catch (error) {
      console.error('Error opening dispute dialog:', error);
    }
  }, [formResources]);

  const handleDisputeSubmit = useCallback(
    async (title: string, receiverId: number) => {
      const selectedResources = [...formResources.values()].flat();
      const resourceIds = selectedResources.map((resource) => resource.id);

      if (resourceIds.length === 0) {
        return;
      }

      try {
        const user = getUser();
        await fetchWrapper.post(urls.DevOps.createDisputeV2, {
          id_receiver: receiverId,
          sender: user?.username,
          title: title,
          type: 'Resource',
          resource_ids: resourceIds,
          action_center_id: null,
        });

        const maxDisputeData = await fetchWrapper.get(urls.DevOps.getMaxDispute);
        const disputeId = maxDisputeData[0]?.disputeid;

        if (disputeId) {
          const disputesList = await fetchWrapper.get(urls.DevOps.disputesList);
          const createdDispute = disputesList.filter(
            (d: any) => d.id === disputeId,
          );
          if (createdDispute.length > 0) {
            setDispute(createdDispute);
            setDisputeDialogOpen(false);
            setDisputeModalOpen(true);
            setFormResources(new Map());
          }
        }
      } catch (error) {
        console.error('Error creating dispute:', error);
      }
    },
    [formResources],
  );


  const renderResourceGroup = (rg: any, parentKey: string) => (
    <Accordion
      key={rg.id ?? `${parentKey}-${rg.resource_group_name}`}
      expanded={expandedResourceGroups.get(rg.id) ?? false}
      onChange={(_, isExpanded) =>
        handleResourceGroupToggle(rg.id, isExpanded)
      }>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginLeft: 6,
            flexGrow: 1,
          }}>
          <Checkbox
            checked={
              formResources.get(rg.id ?? rg.resource_group_name)?.length ===
                rg.resources.length && rg.resources.length > 0
            }
            indeterminate={
              formResources.get(rg.id ?? rg.resource_group_name)?.length > 0 &&
              formResources.get(rg.id ?? rg.resource_group_name)?.length <
                rg.resources.length
            }
            onChange={(e, checked) => {
              e.stopPropagation();
              handleSelectionChange(
                checked ? rg.resources.map((item: any) => item.id) : [],
                {
                  id: rg.id,
                  resource_group_name: rg.resource_group_name,
                  resources: rg.resources,
                },
              );
            }}
            onClick={(e) => e.stopPropagation()}
          />

          <Typography variant="body2" fontSize={16}>
            {rg.resource_group_name +
              ' - (Cost: ' +
                          formatCurrencyNoUnit(Number(rg.cost ?? 0)) +
              ')'}
            {hasRecommendations(rg.resources) && (
              <Tooltip title={`${getRecommendationCount(rg.resources)} recommendation${getRecommendationCount(rg.resources) > 1 ? 's' : ''} in this resource group`}>
                <DsIcon name="dollar" size={18} style={{ color: T.warn, marginLeft: '8px', verticalAlign: 'middle' }} />
              </Tooltip>
            )}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        {expandedResourceGroups.get(rg.id) && (
          <div
            style={{
              width: '100%',
            }}>
            {(() => {
              if (tagsLoading && !tagMap.has(rg.id)) {
                return (
                  <Typography
                    variant="caption"
                    sx={{ marginBottom: 2, display: 'block' }}>
                    Loading tags...
                  </Typography>
                );
              }
              
              if (tagMap.has(rg.id) && tagMap.get(rg.id)?.length && tagMap.get(rg.id)!.length > 0) {
                return (
                  <ResourceGroupChipContainer>
                    {tagMap.get(rg.id)?.map((tag: any) => (
                      <Chip
                        key={tag.key}
                        label={`${tag.key}: ${tag.value}`}
                        size="small"
                      />
                    ))}
                  </ResourceGroupChipContainer>
                );
              }
              
              if (tagMap.has(rg.id) && tagMap.get(rg.id)?.length === 0) {
                return (
                  <Typography
                    variant="caption"
                    sx={{ marginBottom: 2, display: 'block' }}>
                    No tags found for this resource group.
                  </Typography>
                );
              }
              
              return null;
            })()}

            <AppDataGrid
              checkboxSelection
              density="compact"
              rows={rg.resources.map((resource: any) => ({
                ...resource,
                __recommendationsClick: handleRecommendationsClick,
              }))}
              columns={getColumnsForGrouping(groupBy, handleResourceClick)}
              rowSelectionModel={
                formResources
                  .get(rg.id ?? rg.resource_group_name)
                  ?.filter((item: any) =>
                    rg.resources.some((rgItem: any) => rgItem.id === item.id),
                  )
                  .map((item: any) => item.id) ?? []
              }
              onRowSelectionModelChange={(ids) => {
                handleSelectionChange(ids, {
                  id: rg.id,
                  resource_group_name: rg.resource_group_name,
                  resources: rg.resources,
                });
              }}
              key={`datagrid-${rg.id}-${resourceFilter}`}
            />
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );

  const Content = () => (
    <MainContentContainer>
      <FilterContainer>
        <b>
          {groupBy === 'hierarchy'
            ? 'Subscriptions / Resource Groups / Resources'
            : 'Systems / Resource Groups / Resources'}
        </b>

        <SearchInput
          onDebouncedChange={handleDebouncedSearchChange}
          label="Search"
          debounceDelay={500}
          value={resourceFilter}
        />
      </FilterContainer>

      {alert && (
        <Alert severity="success" style={{ marginTop: 10, marginBottom: 10 }}>
          Assignment Successful
        </Alert>
      )}

      {paginatedItems.length === 0 && (
        <Typography sx={{ marginTop: '20px', textAlign: 'center' }}>
          No items found matching your criteria.
        </Typography>
      )}

      {groupBy === 'hierarchy' &&
        paginatedItems.map((subscription: any) => {
          const { groups, totalCost } =
            getResourceGroupsForSubscription(subscription);

          const lowerCaseFilter = resourceFilter.toLowerCase();

          const filteredGroups = groups
            .filter((rg: any) => {
              const rgMatches =
                rg.resource_group_name
                  .toLowerCase()
                  .includes(lowerCaseFilter) ??
                rg.subscription_id.toLowerCase().includes(lowerCaseFilter) ??
                subscription.toLowerCase().includes(lowerCaseFilter);

              const hasMatchingResources = rg.resources.length > 0;

              return rgMatches || hasMatchingResources;
            })
            .sort((a: any, b: any) => Number(b.cost) - Number(a.cost));

          return (
            <Accordion
              key={subscription as string}
              expanded={
                expandedSubscriptions.get(subscription as string) ?? false
              }
              onChange={(_, isExpanded) =>
                handleSubscriptionToggle(subscription as string, isExpanded)
              }>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontSize={16}>
                  {subscription} - Cost: {formatCurrencyNoUnit(totalCost)}
                  {hasRecommendations(groups.flatMap((rg: any) => rg.resources)) && (
                    <Tooltip title={`${getRecommendationCount(groups.flatMap((rg: any) => rg.resources))} recommendation${getRecommendationCount(groups.flatMap((rg: any) => rg.resources)) > 1 ? 's' : ''} in this subscription`}>
                      <DsIcon name="dollar" size={18} style={{ color: T.warn, marginLeft: '8px', verticalAlign: 'middle' }} />
                    </Tooltip>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {filteredGroups.length === 0 && (
                  <Typography variant="body2" sx={{ marginLeft: 2 }}>
                    No items matching filter in this subscription.
                  </Typography>
                )}
                {filteredGroups.map((rg: any) =>
                  renderResourceGroup(rg, subscription),
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}

      {groupBy === 'systems' &&
        paginatedItems.map((systemName: any) => {
          const { groups, totalCost } = getResourceGroupsForSystem(systemName);

          const lowerCaseFilter = resourceFilter.toLowerCase();

          const filteredGroups = groups.filter((rg: any) => {
            const rgMatches =
              rg.resource_group_name.toLowerCase().includes(lowerCaseFilter) ||
              systemName.toLowerCase().includes(lowerCaseFilter);

            const hasMatchingResources = rg.resources.length > 0;

            return rgMatches || hasMatchingResources;
          });

          return (
            <Accordion
              key={systemName as string}
              expanded={expandedSystems.get(systemName as string) ?? false}
              onChange={(_, isExpanded) =>
                handleSystemToggle(systemName as string, isExpanded)
              }>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontSize={16}>
                  {systemName} - Cost: {formatCurrencyNoUnit(totalCost)}
                  {hasRecommendations(groups.flatMap((rg: any) => rg.resources)) && (
                    <Tooltip title={`${getRecommendationCount(groups.flatMap((rg: any) => rg.resources))} recommendation${getRecommendationCount(groups.flatMap((rg: any) => rg.resources)) > 1 ? 's' : ''} in this system`}>
                      <DsIcon name="dollar" size={18} style={{ color: T.warn, marginLeft: '8px', verticalAlign: 'middle' }} />
                    </Tooltip>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {filteredGroups.length === 0 && (
                  <Typography variant="body2" sx={{ marginLeft: 2 }}>
                    No items matching filter in this system.
                  </Typography>
                )}
                {filteredGroups.map((rg: any) =>
                  renderResourceGroup(rg, systemName),
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}

      {totalPages > 1 && (
        <PaginationContainer>
          <Button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
            disabled={currentPage === 0}>
            Previous
          </Button>
          <div style={{ margin: '0 15px' }}>
            Page {currentPage + 1} of {totalPages}
          </div>
          <Button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
            }
            disabled={currentPage >= totalPages - 1}>
            Next
          </Button>
        </PaginationContainer>
      )}

      <SwitchBox sx={{ mt: 3 }}>
        <Typography fontSize={14} fontWeight="bold">
          {showNonPendingResources
            ? 'Attested and Rejected resources shown'
            : 'Attested and Rejected resources hidden'}
        </Typography>
        <Switch
          checked={showNonPendingResources}
          onChange={(e) => handleStatusSwitchChange(e.target.checked)}
          color="primary"
        />
      </SwitchBox>
    </MainContentContainer>
  );

  return (
    <AppCard
      className="card100"
      title="Resource Attestation"
      icon={<BarChartOutlinedIcon color="primary" />}
      headerRight={
        onGroupByChange && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ToggleButtonGroup
              value={groupBy}
              exclusive
              onChange={handleGroupByChange}
              size="small">
              <ToggleButton value="systems">
                Group by systems
              </ToggleButton>
              <ToggleButton value="hierarchy">Group by resource hierarchy</ToggleButton>
            </ToggleButtonGroup>
          </div>
        )
      }>
      <CardContainer>
        <AssignmentSection
          isReadonly={isReadonly}
          formResources={formResources}
          resourcesLength={resourcesLength}
          resourcesSelected={resourcesSelected}
          handleAttestatation={handleAttestatation}
          handleRejectClick={handleRejectClick}
          handleOpenDisputeDialog={handleOpenDisputeDialog}>
          <Content />
        </AssignmentSection>
      </CardContainer>

      <DisputeDialog
        open={disputeDialogOpen}
        onClose={() => {
          setDisputeDialogOpen(false);
        }}
        onSubmit={handleDisputeSubmit}
        defaultReceiverId={defaultReceiverId}
        currentUserId={getUser()?.id || 0}
        showUserSelector={showUserSelector}
        availableUsers={availableUsers}
      />

      {disputeModalOpen && dispute[0] && (
        <DisputesModal
          Dispute={dispute[0]}
          IsModalOpened={disputeModalOpen}
          onCloseModal={() => {
            setDisputeModalOpen(false);
            setDispute([]);
          }}
        />
      )}

      <ReasonForRejectionDialog
        open={rejectionDialogOpen}
        onClose={handleRejectDialogClose}
        onReject={handleRejectConfirm}
      />

      <RecommendationsListModal
        open={recommendationsModalOpen}
        onClose={handleRecommendationsModalClose}
        recommendations={selectedResourceRecommendations?.recommendations || []}
        resourceName={selectedResourceRecommendations?.resourceName || ''}
      />

      <ResourceDetailsModal
        open={resourceModalOpen}
        onClose={() => {
          setResourceModalOpen(false);
          setSelectedResourceId(null);
        }}
        resourceId={selectedResourceId}
        currency={getUser()?.default_currency || 'USD'}
      />
    </AppCard>
  );
}

export default AttestationTable;
